/**
 * briefStageState
 *
 * Pure derivation logic for the brief's production pipeline shown as a
 * batch-progress indicator: DRAFT -> CAPTURING -> EDITING -> COMPLETE.
 * No React, no fetching: status in, view-model out, so the progression
 * math is unit-testable in isolation from its chrome.
 *
 * Colors reuse the shipped ``BRIEF_STATUS_CONFIG`` dot classes so the rail
 * speaks the same status-dot language as the brief card and badge.
 */

import { BriefStatus } from "./types";
import { BRIEF_STATUS_CONFIG } from "./briefStatusConfig";

/** Ordered pipeline stages, earliest first. */
export const BRIEF_STAGE_ORDER: BriefStatus[] = [
  "DRAFT",
  "CAPTURING",
  "EDITING",
  "COMPLETE",
];

export interface BriefStageNode {
  id: BriefStatus;
  label: string;
  /** Tailwind ``bg-*`` dot class drawn from BRIEF_STATUS_CONFIG. */
  dotColor: string;
  /** This stage's order index has been reached (<= current). */
  reached: boolean;
  /** This stage is the brief's current status. */
  current: boolean;
  /** This stage is strictly behind the current one (drives the check glyph). */
  done: boolean;
}

export interface BriefProgress {
  /** Current stage order index (0..total-1). */
  index: number;
  /** Number of pipeline stages. */
  total: number;
  /** Stages strictly completed before the current one. */
  completedStages: number;
  /** Linear progress 0..100, COMPLETE => 100. */
  percent: number;
}

/** Resolve a status to its order index, defaulting unknowns to the start. */
export function briefStageIndex(status: BriefStatus): number {
  const index = BRIEF_STAGE_ORDER.indexOf(status);
  return index < 0 ? 0 : index;
}

/** Build the ordered 4-stage rail view-model for the given status. */
export function deriveBriefStages(status: BriefStatus): BriefStageNode[] {
  const current = briefStageIndex(status);
  return BRIEF_STAGE_ORDER.map((id, index) => ({
    id,
    label: BRIEF_STATUS_CONFIG[id].label,
    dotColor: BRIEF_STATUS_CONFIG[id].dot,
    reached: index <= current,
    current: index === current,
    done: index < current,
  }));
}

/** Linear batch progress for the brief's pipeline. */
export function briefProgress(status: BriefStatus): BriefProgress {
  const index = briefStageIndex(status);
  const total = BRIEF_STAGE_ORDER.length;
  return {
    index,
    total,
    completedStages: index,
    percent: Math.round((index / (total - 1)) * 100),
  };
}
